import { useMutation, useQuery } from "@tanstack/react-query"
import { Eye, EyeOff, WandSparkles } from "lucide-react"
import { type FormEvent, useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { FECHA_MAXIMA, FECHA_MINIMA, fechaFueraDeRango } from "@/lib/fechas"

import { fuerzaDe, generarClave } from "./fuerza"
import { MedidorFuerza } from "./MedidorFuerza"
import type { Categoria, Credencial } from "./tipos"

type ClienteBreve = { id: number; razon_social: string }

type Borrador = {
  categoria_id: string
  cliente_id: string
  nombre: string
  url: string
  usuario: string
  documento: string
  correo: string
  telefono: string
  clave: string
  notas: string
  reemplazarNotas: boolean
  ultimo_cambio: string
  vence_en: string
}

function inicial(
  c: Credencial | null,
  categoriaPorOmision: number | null,
  categorias: Categoria[],
): Borrador {
  return {
    categoria_id: String(c?.categoria_id ?? categoriaPorOmision ?? categorias[0]?.id ?? ""),
    cliente_id: c?.cliente_id ? String(c.cliente_id) : "",
    nombre: c?.nombre ?? "",
    url: c?.url ?? "",
    usuario: c?.usuario ?? "",
    documento: c?.documento ?? "",
    correo: c?.correo ?? "",
    telefono: c?.telefono ?? "",
    clave: "",
    notas: "",
    reemplazarNotas: false,
    ultimo_cambio: c?.ultimo_cambio ?? "",
    vence_en: c?.vence_en ?? "",
  }
}

/**
 * Registrar o cambiar una credencial.
 *
 * ⚠ **Al editar, la contraseña vacía conserva la guardada** —no se puede enseñar aquí:
 * va cifrada— y **las notas sólo viajan si se decidió reemplazarlas**. El sistema de
 * referencia mandaba siempre unas notas vacías desde un formulario que no podía
 * mostrarlas, y cada edición las borraba sin avisar.
 *
 * ⚠ **El cliente es opcional**: casi todas las credenciales son de la propia agencia.
 * Si quien edita no puede ver el catálogo de clientes, el selector no sale.
 */
export function FormularioCredencial({
  credencial,
  categorias,
  categoriaPorOmision,
  onCerrar,
  onGuardado,
}: {
  credencial: Credencial | null
  categorias: Categoria[]
  categoriaPorOmision: number | null
  onCerrar: () => void
  onGuardado: () => void
}) {
  const id = useId()
  const [b, setB] = useState<Borrador>(() => inicial(credencial, categoriaPorOmision, categorias))
  const [verClave, setVerClave] = useState(false)
  const [generada, setGenerada] = useState(false)

  const clientes = useQuery({
    queryKey: ["clientes"],
    queryFn: () => api.get<ClienteBreve[]>("/api/v1/clientes?activo=true"),
    retry: false,
  })

  function cambiar<K extends keyof Borrador>(campo: K, valor: Borrador[K]) {
    setB((previo) => ({ ...previo, [campo]: valor }))
  }

  const verNotas = credencial === null || !credencial.tiene_notas || b.reemplazarNotas

  const guardar = useMutation({
    mutationFn: () => {
      const comun = {
        categoria_id: Number(b.categoria_id),
        cliente_id: b.cliente_id === "" ? null : Number(b.cliente_id),
        nombre: b.nombre,
        url: b.url.trim() || null,
        usuario: b.usuario,
        documento: b.documento.trim() || null,
        correo: b.correo.trim() || null,
        telefono: b.telefono.trim() || null,
        vence_en: b.vence_en || null,
      }
      if (credencial === null) {
        return api.post<Credencial>("/api/v1/credenciales", {
          ...comun,
          clave: b.clave,
          notas: b.notas.trim() || null,
          ultimo_cambio: b.ultimo_cambio || null,
        })
      }
      const cambios: Record<string, unknown> = { ...comun }
      if (b.clave !== "") cambios.clave = b.clave
      if (b.ultimo_cambio !== (credencial.ultimo_cambio ?? "")) {
        cambios.ultimo_cambio = b.ultimo_cambio || null
      }
      if (verNotas && (credencial.tiene_notas || b.notas.trim() !== "")) {
        cambios.notas = b.notas.trim() || null
      }
      return api.patch<Credencial>(`/api/v1/credenciales/${credencial.id}`, cambios)
    },
    onSuccess: () => onGuardado(),
  })

  const falta =
    b.nombre.trim() === "" ||
    b.usuario.trim() === "" ||
    b.categoria_id === "" ||
    (credencial === null && b.clave === "")
  const fechaMala = fechaFueraDeRango(b.ultimo_cambio) || fechaFueraDeRango(b.vence_en)

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (!falta && !fechaMala) guardar.mutate()
  }

  const etiqueta = "mb-1 block text-xs font-medium uppercase"

  return (
    <Modal
      abierto={true}
      titulo={credencial === null ? "Nueva credencial" : `Editar «${credencial.nombre}»`}
      descripcion="La contraseña y las notas se guardan cifradas. Lo demás sirve para reconocer la ficha."
      onCerrar={onCerrar}
    >
      <form onSubmit={enviar} className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor={`${id}-nombre`} className={etiqueta}>
            Nombre
          </label>
          <input
            id={`${id}-nombre`}
            value={b.nombre}
            onChange={(e) => cambiar("nombre", e.target.value)}
            maxLength={120}
            placeholder="Banesco en línea — cuenta de la agencia"
            className="campo"
            required={true}
          />
        </div>

        <div>
          <label htmlFor={`${id}-categoria`} className={etiqueta}>
            Categoría
          </label>
          <select
            id={`${id}-categoria`}
            value={b.categoria_id}
            onChange={(e) => cambiar("categoria_id", e.target.value)}
            className="campo"
          >
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </div>

        {clientes.isSuccess && (
          <div>
            <label htmlFor={`${id}-cliente`} className={etiqueta}>
              Cliente (opcional)
            </label>
            <select
              id={`${id}-cliente`}
              value={b.cliente_id}
              onChange={(e) => cambiar("cliente_id", e.target.value)}
              className="campo"
            >
              <option value="">De la propia agencia</option>
              {clientes.data.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razon_social}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="sm:col-span-2">
          <label htmlFor={`${id}-url`} className={etiqueta}>
            Dirección web
          </label>
          <input
            id={`${id}-url`}
            value={b.url}
            onChange={(e) => cambiar("url", e.target.value)}
            maxLength={240}
            inputMode="url"
            placeholder="banesco.com"
            className="campo"
          />
        </div>

        <div>
          <label htmlFor={`${id}-usuario`} className={etiqueta}>
            Usuario
          </label>
          <input
            id={`${id}-usuario`}
            value={b.usuario}
            onChange={(e) => cambiar("usuario", e.target.value)}
            maxLength={160}
            autoComplete="off"
            spellCheck={false}
            className="campo"
            required={true}
          />
        </div>

        <div>
          <label htmlFor={`${id}-documento`} className={etiqueta}>
            Cédula o RIF
          </label>
          <input
            id={`${id}-documento`}
            value={b.documento}
            onChange={(e) => cambiar("documento", e.target.value)}
            maxLength={30}
            className="campo"
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor={`${id}-clave`} className={etiqueta}>
            {credencial === null ? "Contraseña" : "Contraseña nueva"}
          </label>
          <div className="flex gap-2">
            <input
              id={`${id}-clave`}
              type={verClave ? "text" : "password"}
              autoComplete="new-password"
              spellCheck={false}
              value={b.clave}
              onChange={(e) => {
                cambiar("clave", e.target.value)
                setGenerada(false)
              }}
              maxLength={500}
              placeholder={credencial === null ? "" : "Vacía conserva la guardada"}
              className="campo min-w-0 flex-1 font-mono"
              required={credencial === null}
            />
            <button
              type="button"
              onClick={() => setVerClave((v) => !v)}
              aria-label={verClave ? "Ocultar lo escrito" : "Ver lo escrito"}
              aria-pressed={verClave}
              className="boton-secundario shrink-0 px-2"
            >
              {verClave ? (
                <EyeOff size={16} aria-hidden={true} />
              ) : (
                <Eye size={16} aria-hidden={true} />
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                cambiar("clave", generarClave())
                setVerClave(true)
                setGenerada(true)
              }}
              aria-label="Generar una contraseña"
              title="Generar una contraseña"
              className="boton-secundario shrink-0 px-2"
            >
              <WandSparkles size={16} aria-hidden={true} />
            </button>
          </div>
          {b.clave !== "" && (
            <div className="mt-1.5">
              <MedidorFuerza fuerza={fuerzaDe(b.clave)} />
            </div>
          )}
          {generada && (
            <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
              Cámbiala también en el portal: aquí sólo se guarda.
            </p>
          )}
        </div>

        <div>
          <label htmlFor={`${id}-correo`} className={etiqueta}>
            Correo de la cuenta
          </label>
          <input
            id={`${id}-correo`}
            type="email"
            value={b.correo}
            onChange={(e) => cambiar("correo", e.target.value)}
            maxLength={160}
            className="campo"
          />
        </div>

        <div>
          <label htmlFor={`${id}-telefono`} className={etiqueta}>
            Teléfono del segundo factor
          </label>
          <input
            id={`${id}-telefono`}
            type="tel"
            value={b.telefono}
            onChange={(e) => cambiar("telefono", e.target.value)}
            maxLength={30}
            className="campo"
          />
        </div>

        <div className="sm:col-span-2">
          {credencial?.tiene_notas && (
            <label className="mb-2 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={b.reemplazarNotas}
                onChange={(e) => cambiar("reemplazarNotas", e.target.checked)}
              />
              <span>
                Reemplazar las notas guardadas
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  No se enseñan aquí porque van cifradas. Si las dejas vacías, se borran.
                </span>
              </span>
            </label>
          )}
          {verNotas && (
            <>
              <label htmlFor={`${id}-notas`} className={etiqueta}>
                Notas (se guardan cifradas)
              </label>
              <textarea
                id={`${id}-notas`}
                rows={3}
                value={b.notas}
                onChange={(e) => cambiar("notas", e.target.value)}
                maxLength={2000}
                placeholder="Preguntas de seguridad, PIN, a quién llamar si se bloquea…"
                className="campo h-auto py-2"
              />
            </>
          )}
        </div>

        <div>
          <label htmlFor={`${id}-cambio`} className={etiqueta}>
            Último cambio
          </label>
          <input
            id={`${id}-cambio`}
            type="date"
            min={FECHA_MINIMA}
            max={FECHA_MAXIMA}
            value={b.ultimo_cambio}
            onChange={(e) => cambiar("ultimo_cambio", e.target.value)}
            className="campo tabular-nums"
          />
          <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            Vacío, se pone la fecha de hoy al guardar una contraseña.
          </p>
        </div>

        <div>
          <label htmlFor={`${id}-vence`} className={etiqueta}>
            Vence el
          </label>
          <input
            id={`${id}-vence`}
            type="date"
            min={FECHA_MINIMA}
            max={FECHA_MAXIMA}
            value={b.vence_en}
            onChange={(e) => cambiar("vence_en", e.target.value)}
            className="campo tabular-nums"
          />
        </div>

        {(guardar.isError || fechaMala) && (
          <p role="alert" className="text-sm sm:col-span-2" style={{ color: "var(--destructive)" }}>
            {fechaMala
              ? "Una de las fechas está fuera de rango."
              : guardar.error instanceof ErrorApi
                ? guardar.error.message
                : "No se pudo guardar."}
          </p>
        )}

        <div className="flex justify-end gap-2 sm:col-span-2">
          <Boton variante="fantasma" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            variante="primario"
            type="submit"
            disabled={falta || fechaMala || guardar.isPending}
          >
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
